import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { apiClient } from '../api/client';
import { useAuthStore } from '../store/auth';
import { TeamManagement } from './TeamManagement';
import { PhreaticBilling } from './phreatic/PhreaticBilling';

/**
 * Organization Admin cockpit — the org owner's home after onboarding.
 *   - Admin          → org + owner info (who registered the org), quick links
 *   - Team & Seats   → TeamManagement (roles, seats, invites)
 *   - Billing & Tokens → PhreaticBilling (plan/tier, seats, tokens, dashboard license)
 */
type Tab = 'admin' | 'team' | 'billing';

const TABS: { id: Tab; label: string }[] = [
  { id: 'admin', label: 'Admin' },
  { id: 'team', label: 'Team & Seats' },
  { id: 'billing', label: 'Billing & Tokens' },
];

function tierLabel(t: string | undefined): string {
  return t === 'dashboard' ? 'Dashboard License'
    : t === 'command' ? 'Onboarded — CARI Reports'
    : 'Not Onboarded';
}

function AdminTab({ goBilling }: { goBilling: () => void }) {
  const user = useAuthStore((s) => s.user);
  const [org, setOrg] = useState<any>(null);
  const [license, setLicense] = useState<any>(null);

  useEffect(() => {
    apiClient.get('/clients/current').then((r) => setOrg(r.data)).catch(() => {});
    apiClient.get('/phreatic/billing/license').then((r) => setLicense(r.data)).catch(() => {});
  }, []);

  return (
    <div className="space-y-4">
      {/* Organization */}
      <div className="border border-white/10 bg-white/[0.05] backdrop-blur-md shadow-[inset_0_1px_0_0_rgba(255,255,255,0.06)] rounded-lg p-6">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="text-[10px] text-mono-500 uppercase tracking-wider mb-1">Organization</div>
            <div className="text-xl font-semibold text-mono-100">{org?.name || '—'}</div>
            <div className="text-xs text-mono-500 mt-1">
              Org code: <span className="text-mono-300 font-mono">{org?.organization_code || '—'}</span>
              {org?.status && <> · {String(org.status).toUpperCase()}</>}
            </div>
          </div>
          <span className="px-2 py-1 text-[10px] rounded border border-cyan-500/40 bg-cyan-500/10 text-cyan-300">
            {tierLabel(license?.effective_tier)}
          </span>
        </div>
        <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-4">
          <div>
            <div className="text-[10px] text-mono-500 uppercase">Seats</div>
            <div className="text-lg font-semibold text-mono-100">{license?.seats_purchased ?? '—'}</div>
          </div>
          {license?.effective_tier !== 'dashboard' && (
            <div className="flex items-end">
              <button onClick={goBilling} className="text-xs text-cyan-300 hover:text-cyan-200 underline">
                Unlock full dashboard →
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Org administrator (the registering owner) */}
      <div className="border border-white/10 bg-white/[0.05] backdrop-blur-md shadow-[inset_0_1px_0_0_rgba(255,255,255,0.06)] rounded-lg p-6">
        <div className="text-[10px] text-mono-500 uppercase tracking-wider mb-2">Org Administrator</div>
        <div className="text-sm text-mono-100">{user?.full_name || org?.primary_contact_name || '—'}</div>
        <div className="text-xs text-mono-400">{user?.email || org?.primary_contact_email || '—'}</div>
        <div className="text-[10px] text-mono-500 mt-1 uppercase tracking-wider">{user?.role || 'admin'}</div>
      </div>
    </div>
  );
}

export function OrgAdmin() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const paymentIntent = params.get('payment_intent');
  const justOnboarded = params.get('onboarded') === '1';
  const [tab, setTab] = useState<Tab>(justOnboarded ? 'billing' : 'admin');
  const [activating, setActivating] = useState<boolean>(!!paymentIntent);
  const effectiveRole = useAuthStore((s) => s.getEffectiveRole());
  const isAdmin = effectiveRole === 'admin' || effectiveRole === 'superadmin';

  // Returned from the in-app checkout → confirm + activate (organic onboarding;
  // same path as the webhook, idempotent), then drop the intent id from the URL.
  useEffect(() => {
    if (!paymentIntent) return;
    apiClient.post('/auth/payment/confirm', { payment_intent_id: paymentIntent })
      .catch(() => {})
      .finally(() => {
        window.history.replaceState({}, '', '/account' + (justOnboarded ? '?onboarded=1' : ''));
        setActivating(false);
      });
  }, [paymentIntent, justOnboarded]);

  // Background — zoomed-in left side of the ARGOS logo, matching the
  // login/signup pages and the landing-page sections. Pinned to the viewport
  // (fixed, all the way in the back) so the admin cards scroll over it.
  const pageBg = (
    <div className="fixed inset-0 z-0 overflow-hidden pointer-events-none bg-mono-700">
      <img
        src="/argos-full-black.png"
        alt=""
        aria-hidden="true"
        className="absolute top-[58%] left-[-5vw] -translate-y-1/2 h-[250vw] md:h-[400vh] w-auto max-w-none object-contain opacity-30 select-none [filter:drop-shadow(0_18px_40px_rgba(0,0,0,0.55))]"
      />
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-[radial-gradient(ellipse_60%_45%_at_50%_25%,_rgba(255,255,255,0.10)_0%,_rgba(255,255,255,0.03)_40%,_transparent_70%)]"
      />
    </div>
  );

  if (activating) {
    return (
      <div className="relative">
        {pageBg}
        <div className="relative z-10 p-6 text-center text-mono-400 text-sm animate-pulse">Activating your account…</div>
      </div>
    );
  }

  // Org admin cockpit is for the org owner / admins only. Invited seats
  // (analyst/viewer) manage nothing here — they use the product + see the
  // shared token balance via the billing page.
  if (!isAdmin) {
    return (
      <div className="relative">
        {pageBg}
        <div className="relative z-10 p-6 text-center">
          <p className="text-mono-300 text-sm tracking-widest mb-2">ORGANIZATION ADMIN ACCESS REQUIRED</p>
          <p className="text-mono-500 text-xs mb-4">Contact your organization administrator.</p>
          <Link to="/phreatic/billing" className="text-cyan-400 hover:text-cyan-300 text-xs tracking-wider">
            Go to billing →
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      {pageBg}
      <div className="relative z-10 p-6 space-y-4">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-mono-100 font-orbitron tracking-wider">Organization Admin</h1>
          <p className="text-mono-400 text-sm mt-1">Manage your team, seats, billing, and tokens.</p>
        </div>
        <Link
          to="/phreatic/command"
          className="px-4 py-2 text-sm rounded bg-cyan-500/20 text-cyan-300 border border-cyan-500/40 hover:bg-cyan-500/30 whitespace-nowrap"
        >
          Open Phreatic →
        </Link>
      </div>

      {justOnboarded && (
        <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3 text-emerald-300 text-sm">
          You're onboarded. Buy tokens to start snipping, invite your team, and (optionally) unlock the full dashboard below.
        </div>
      )}

      <div className="flex gap-2 border-b border-mono-800">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-xs tracking-widest transition-colors ${
              tab === t.id ? 'bg-cyan-500 text-black' : 'text-mono-400 hover:text-cyan-400'
            }`}
          >
            {t.label.toUpperCase()}
          </button>
        ))}
      </div>

      <div>
        {tab === 'admin' && <AdminTab goBilling={() => setTab('billing')} />}
        {tab === 'team' && <TeamManagement />}
        {tab === 'billing' && <PhreaticBilling />}
      </div>
      </div>
    </div>
  );
}
